import {
  RedoOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Col, ConfigProvider, Form, FormInstance, Row } from "antd";
import { BasedObservedViews } from "app/based/basedObservedViews/BasedObservedViews";
import { IBaseViews } from "app/based/baseViews/BaseViews";
import { FilterComponent } from "components/generalComponents/filterComponents/FilterComponents";
import PillTabs from "components/generalComponents/pillTabs/PillTabs";
import { createRef, ReactNode } from "react";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { isLsgdChainOrStoreUser } from "utils/helpersInTs/PartnerRoleForTrans";
import { strings } from "utils/localizedStrings";
import { GeneralConstants } from "../constants/GeneralConstants";
import {
  GeneralRangeType,
  GeneralTabType,
  IGeneralExportEffect,
  IGeneralReportCommand,
  IGeneralState,
} from "../interfaces/IGenerals";
import { GeneralViewModel } from "../viewModel/GeneralViewModel";
import GeneralReportTablesComponent from "./components/GeneralReportTablesComponent";
import GeneralStatCardsComponent from "./components/GeneralStatCardsComponent";

type GeneralFilterValues = {
  currency?: string;
  partner?: string;
  staff?: string[];
  store?: string;
  telco?: string[];
};

export default class GeneralFeature extends BasedObservedViews<
  GeneralViewModel,
  {},
  IGeneralState
> {
  private filterForm = createRef<FormInstance>();
  private lastLanguage?: string;
  private lastMenuConfig?: unknown;
  private hasRefreshedRoleAfterPartnerLoaded = false;

  constructor(props: IBaseViews) {
    super(props);
    this.state = {
      activeTab: "revenue",
    };
  }

  private get cachedCapabilities() {
    return productCache?.getCache(CacheKeyEnum.capabilities);
  }

  private get filterValues(): GeneralFilterValues {
    return this.filterForm.current?.getFieldsValue(true) || {};
  }

  private get isAbleToExport(): boolean {
    return this.cachedCapabilities?.some(
      (item) => item.key === CapabilityEnum.exportExcel,
    );
  }

  private reportCommand(
    serviceCode = this.viewModel.observedState.service,
    values: GeneralFilterValues = this.filterValues,
  ): IGeneralReportCommand {
    return {
      serviceCode,
      partnerRefCode: values.partner || undefined,
      telco: Array.isArray(values.telco)
        ? values.telco.filter(Boolean)
        : undefined,
    };
  }

  private refreshReports = (): void => {
    const values = this.filterValues;
    const state = this.viewModel.observedState;
    if (!state.service) return;

    this.connectToVM(this.viewModel.input.refreshReports, {
      serviceCode: state.service,
      reportType: this.viewModel.reportType,
      partnerRefCode: values.partner || undefined,
      telco: values.telco?.filter(Boolean),
      target: "all",
    });
  };

  private refreshDetailReport = (
    range: GeneralRangeType,
    values: GeneralFilterValues = this.filterValues,
  ): void => {
    if (!this.viewModel.observedState.service) return;

    const command = this.reportCommand(
      this.viewModel.observedState.service,
      values,
    );
    const reportType = range === "day" ? 0 : range === "week" ? 1 : 2;

    this.connectToVM(this.viewModel.input.refreshReports, {
      ...command,
      reportType,
      target: "detail",
    });
  };

  private downloadExport = (effect: IGeneralExportEffect): void => {
    const url = window.URL.createObjectURL(effect.blob);
    const link = document.createElement("a");

    try {
      link.href = url;
      link.setAttribute("download", effect.fileName);
      document.body.appendChild(link);
      link.click();
    } finally {
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  private handleServiceChange = (value: string | number): void => {
    const service = String(value);
    this.connectToVM(this.viewModel.input.updateFilters, { service });
    this.refreshReports();
  };

  private handleRangeChange = (range: GeneralRangeType): void => {
    this.connectToVM(this.viewModel.input.updateFilters, { range });
    this.refreshDetailReport(range);
  };

  private handlePartnerChange = (partner: string): void => {
    this.filterForm.current?.setFieldsValue({ store: "", staff: [""] });
    this.connectToVM(this.viewModel.input.updateRoleFilters, {
      target: "partner",
      partnerValue: partner || "",
    });
    this.refreshReports();
  };

  private handleStoreChange = (store: string): void => {
    this.filterForm.current?.setFieldValue("staff", [""]);
    this.connectToVM(this.viewModel.input.updateRoleFilters, {
      target: "store",
      storeValue: store || "",
    });
    this.refreshReports();
  };

  private handleStaffChange = (staff: string[]): void => {
    this.connectToVM(this.viewModel.input.selectStaff, staff || []);
    this.refreshReports();
  };

  private handleFilterValuesChange = (
    changedValues: GeneralFilterValues,
    values: GeneralFilterValues,
  ): void => {
    if (Object.prototype.hasOwnProperty.call(changedValues, "currency")) {
      this.connectToVM(this.viewModel.input.updateFilters, {
        currency: values.currency || "VND",
      });
      return;
    }

    if (
      Object.prototype.hasOwnProperty.call(changedValues, "partner") ||
      Object.prototype.hasOwnProperty.call(changedValues, "store") ||
      Object.prototype.hasOwnProperty.call(changedValues, "staff")
    ) {
      return;
    }

    this.refreshReports();
  };

  private handleSearch = (): void => {
    this.refreshReports();
  };

  private handleReset = (): void => {
    const { currencyOptions } = this.viewModel.observedState;
    const currency = currencyOptions.some((item) => item?.value === "VND")
      ? "VND"
      : String(currencyOptions[0]?.value || "VND");

    this.filterForm.current?.resetFields([
      "partner",
      "telco",
      "currency",
      "store",
      "staff",
    ]);
    this.filterForm.current?.setFieldsValue({
      partner: "",
      telco: [""],
      currency,
      store: "",
      staff: [""],
    });
    this.connectToVM(this.viewModel.input.updateFilters, {
      currency,
      selectedPartnerType: 0,
      selectedPartnerCode: "",
      selectedProviderCode: "",
      isStore: false,
      selectedStores: [],
      selectedStaffs: [],
    });
    this.connectToVM(this.viewModel.input.updateRoleFilters, {
      target: "role",
    });
    this.refreshReports();
  };

  private handleExport = (): void => {
    const state = this.viewModel.observedState;
    if (!state.service) return;

    const command = this.reportCommand(state.service);

    this.connectToVM(this.viewModel.input.exportTransactionDetailReport, {
      ...command,
      reportType: this.viewModel.reportType,
      extras: {
        store: state.selectedStores,
        staff: state.selectedStaffs,
      },
      fileName: `dashboard_${state.service}_${state.range}.xlsx`,
    });
  };

  private handleTabChange = (activeTab: GeneralTabType): void => {
    this.setState({ activeTab });
  };

  private syncCurrencyFilter = (): void => {
    const form = this.filterForm.current;
    const { currency, currencyOptions } = this.viewModel.observedState;
    const hasCurrencyOption = currencyOptions.some(
      (item) => String(item?.value || "") === currency,
    );

    if (
      !form ||
      !currency ||
      !hasCurrencyOption ||
      form.getFieldValue("currency") === currency
    ) {
      return;
    }

    form.setFieldValue("currency", currency);
  };

  protected override bindViewModel(): void {
    super.bindViewModel();

    this.subscribeToVM(this.viewModel.output.onError, (message) => {
      if (message) getMessage(message, 3, "error");
    });
    this.subscribeToVM(this.viewModel.output.onExportSuccess, (effect) => {
      if (effect) this.downloadExport(effect);
    });
    this.subscribeToVM(this.viewModel.output.onServiceReady, (service) => {
      if (service) this.refreshReports();
    });
    this.subscribeToVM(
      this.viewModel.output.onSyncPartnerRoleFilters,
      (effect) => {
        if (!effect) return;
        this.filterForm.current?.setFieldsValue({
          partner: effect.partner,
          store: effect.store || "",
          staff: [""],
        });
      },
    );

    this.lastLanguage = this.context?.language;
    this.lastMenuConfig = this.context?.menuConfig;
    this.filterForm.current?.setFieldValue("currency", "VND");

    // Initial independent lookups are intentionally triggered in parallel.
    this.connectToVM(this.viewModel.input.fetchService, undefined);
    this.connectToVM(this.viewModel.input.fetchPartnerList, {
      page: 0,
      limit: 500,
      status: 0,
    });
    this.connectToVM(this.viewModel.input.fetchCarrierList, {
      page: 0,
      limit: 100,
    });
    this.connectToVM(this.viewModel.input.updateRoleFilters, {
      target: "role",
    });
  }

  protected override update(): void {
    this.syncCurrencyFilter();
    if (this.lastLanguage !== this.context?.language) {
      this.lastLanguage = this.context?.language;
      this.connectToVM(this.viewModel.input.refreshLocalizedOptions, undefined);
    }

    if (this.lastMenuConfig !== this.context?.menuConfig) {
      this.lastMenuConfig = this.context?.menuConfig;
      this.connectToVM(this.viewModel.input.fetchService, undefined);
    }

    const { partnerList } = this.viewModel.observedState;
    if (
      !this.hasRefreshedRoleAfterPartnerLoaded &&
      partnerList.length > 0 &&
      isLsgdChainOrStoreUser(this.context?.user)
    ) {
      this.hasRefreshedRoleAfterPartnerLoaded = true;
      this.refreshReports();
    }
  }

  render(): ReactNode {
    const {
      carrierList,
      currency,
      currencyOptions,
      exportLoading,
      isStore,
      partnerList,
      periodLabels,
      processedRevenueData,
      processedStatCards,
      processedTableData,
      range,
      selectedPartnerType,
      selectedStores,
      service,
      serviceOptions,
      staffLoading,
      staffOptions,
      storeOptions,
      transactionDetailReportLoading,
      transactionReportLoading,
    } = this.viewModel.observedState;
    const hasGeneralCapability = this.cachedCapabilities?.some(
      (item) => item?.key === CapabilityEnum.general,
    );
    const { isLargeScreen } = this.context;
    const hasSelectedService = !!service && service !== "ALL";
    const hasExtendedFilters = selectedPartnerType === 1 && isStore;
    const filters = GeneralConstants.filters(
      strings,
      partnerList,
      carrierList,
      currencyOptions,
      this.handlePartnerChange,
      storeOptions,
      staffOptions,
      selectedPartnerType,
      isStore,
      selectedStores.length === 0,
      this.handleStoreChange,
      this.handleStaffChange,
      staffLoading,
    );

    return (
      <Form
        ref={this.filterForm}
        initialValues={{ currency: "VND" }}
        onValuesChange={this.handleFilterValuesChange}
      >
        <Row gutter={[8, 12]} className="bg-transparent p-4">
          <Col span={24}>
            <div className="flex flex-col gap-1 mb-1">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
                {strings.General.Title}
              </h2>
              <p className="text-sm text-gray-500">{strings.General.Summary}</p>
            </div>
          </Col>

          <Col span={24}>
            <PillTabs
              value={service}
              onChange={this.handleServiceChange}
              options={serviceOptions.map((item) => ({
                label:
                  strings.GlobalService[
                    item.code as keyof typeof strings.GlobalService
                  ] || item.name,
                value: item.code,
              }))}
            />
          </Col>

          <Col span={24}>
            <div
              className={`general-filter-toolbar${
                hasExtendedFilters ? " general-filter-toolbar--extended" : ""
              }`}
            >
              <Row gutter={[8, 8]} className="general-filter-fields">
                {FilterComponent(filters, this.filterForm.current)}
              </Row>

              <div className="general-filter-actions">
                <div className="general-filter-action-item">
                  <div
                    className="general-filter-action-spacer"
                    aria-hidden="true"
                  />
                  <Form.Item>
                    <div className="general-filter-action-anchor">
                      <ConfigProvider
                        theme={{ token: { colorPrimary: "#909090" } }}
                      >
                        <Button
                          type="primary"
                          size="middle"
                          className="general-filter-action-button"
                          icon={<RedoOutlined />}
                          aria-label={strings.GlobalLabels.Redo}
                          onClick={this.handleReset}
                        >
                          {strings.GlobalLabels.Redo}
                        </Button>
                      </ConfigProvider>
                    </div>
                  </Form.Item>
                </div>

                <div className="general-filter-action-item">
                  <div
                    className="general-filter-action-spacer"
                    aria-hidden="true"
                  />
                  <Form.Item>
                    <div className="general-filter-action-anchor">
                      <ConfigProvider
                        theme={{ token: { colorPrimary: "#2563eb" } }}
                      >
                        <Button
                          type="primary"
                          size="middle"
                          className="general-filter-action-button"
                          icon={<SearchOutlined />}
                          aria-label={strings.GlobalLabels.Search}
                          loading={
                            transactionReportLoading ||
                            transactionDetailReportLoading
                          }
                          onClick={this.handleSearch}
                        >
                          {strings.GlobalLabels.Search}
                        </Button>
                      </ConfigProvider>
                    </div>
                  </Form.Item>
                </div>
              </div>
            </div>
          </Col>

          {hasGeneralCapability ? (
            <Col span={24}>
              <GeneralStatCardsComponent
                language={this.context?.language}
                loading={transactionReportLoading}
                statCards={processedStatCards}
              />
            </Col>
          ) : null}

          <Col span={hasSelectedService ? 24 : 0}>
            <div className="flex items-center justify-between gap-6 mt-2 mb-2 xl:overflow-x-auto overflow-x-scroll">
              <button
                type="button"
                onClick={() => this.handleRangeChange("day")}
                className={`${this.isAbleToExport || !isLargeScreen ? "min-w-[260px]" : "w-1/3"} h-10 rounded-md px-6 font-medium transition-colors ${
                  range === "day"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 border border-gray-300"
                }`}
              >
                {strings.General.ByDate}
              </button>
              <button
                type="button"
                onClick={() => this.handleRangeChange("week")}
                className={`${this.isAbleToExport || !isLargeScreen ? "min-w-[260px]" : "w-1/3"} h-10 rounded-md px-6 font-medium transition-colors ${
                  range === "week"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 border border-gray-300"
                }`}
              >
                {strings.General.ByWeek}
              </button>
              <button
                type="button"
                onClick={() => this.handleRangeChange("month")}
                className={`${this.isAbleToExport || !isLargeScreen ? "min-w-[260px]" : "w-1/3"} h-10 rounded-md px-6 font-medium transition-colors ${
                  range === "month"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 border border-gray-300"
                }`}
              >
                {strings.General.ByMonth}
              </button>
              {this.isAbleToExport ? (
                <Button
                  type="primary"
                  className="h-10 rounded-md flex-1"
                  style={{ backgroundColor: "#16a34a" }}
                  icon={<UploadOutlined />}
                  loading={exportLoading}
                  onClick={this.handleExport}
                >
                  {strings.GlobalLabels.ExcelExporting}
                </Button>
              ) : null}
            </div>
          </Col>

          {hasSelectedService ? (
            <Col span={24}>
              <GeneralReportTablesComponent
                activeTab={this.state.activeTab}
                currency={currency}
                language={this.context?.language}
                loading={transactionDetailReportLoading}
                periodLabels={periodLabels}
                revenueData={processedRevenueData}
                volumeData={processedTableData}
                onTabChange={this.handleTabChange}
              />
            </Col>
          ) : null}
        </Row>
      </Form>
    );
  }
}
